/**
 * URL Transformer utility
 * Transforms blog.gildy.it URLs to www.gildy.it/blog URLs
 */

/**
 * Transform a single URL from blog.gildy.it to www.gildy.it/blog
 * @param {string} url - The URL to transform
 * @param {boolean} isImage - Whether the URL is for an image
 * @param {boolean} forMetaTags - Whether this is for meta tags (Open Graph)
 * @returns {string} - The transformed URL
 */
export function transformSingleUrl(url, isImage = false, forMetaTags = false) {
  if (!url) return url;
  
  // For images, transform WordPress image URLs to use internal image proxy
  if (isImage) {
    // Make sure the URL is absolute
    if (!url.startsWith('http')) {
      url = `https://blog.gildy.it${url.startsWith('/') ? '' : '/'}${url}`;
    }
    
    // Transform WordPress image URLs to use internal image proxy
    if (url.includes('blog.gildy.it/wp-content/uploads/')) {
      // Extract the path after wp-content/uploads/
      const match = url.match(/\/wp-content\/uploads\/(.+)$/);
      if (match) {
        const imagePath = match[1];
        
        // For meta tags (Open Graph), use direct internal URL without external source reference
        if (forMetaTags) {
          return `https://www.gildy.it/images/blog/${imagePath}`;
        }
        
        // For regular usage, use image cache with source parameter
        const filename = imagePath.split('/').pop();
        return `https://www.gildy.it/api/image-cache/${filename}?source=${encodeURIComponent(url)}`;
      }
    }
    
    return url;
  }
  
  // For non-images, transform blog.gildy.it URLs to www.gildy.it/blog URLs
  return url.replace(/https?:\/\/blog\.gildy\.it\//g, 'https://www.gildy.it/blog/');
}

/**
 * Transform HTML content, replacing blog.gildy.it URLs with www.gildy.it/blog URLs
 * Only transforms URLs in href attributes, not in src attributes (for images)
 * @param {string} html - The HTML content to transform
 * @returns {string} - The transformed HTML content
 */
export function transformBlogUrls(html) {
  if (!html) return html;
  
  // Only transform URLs in href attributes, not in src attributes (for images)
  return html.replace(
    /href=["'](https?:\/\/blog\.gildy\.it\/(?!wp-content|wp-json|wp-admin)([^"']*))["']/g, 
    'href="https://www.gildy.it/blog/$2"'
  );
}

// Alias for backward compatibility
export const transformUrl = transformSingleUrl;
export const transformHtmlContent = transformBlogUrls;
